import React from "react";
import { SettingGroup, SettingField, SettingToggle, NumberInput, ExampleBlock } from "./SettingField";
import {
  LookaheadDiagram,
  UseAllSpaceDiagram,
  KeepTypeDiagram,
  UnitsPerRailDiagram,
} from "./FlexibleDiagrams";

// Must match the C# defaults in FlexiblePlacementOptions.cs. Scenarios that
// never used Flexible Oriented have flexibleOptions = null, so the UI always
// reads through these defaults.
export const FLEXIBLE_DEFAULTS = {
  sectionLookahead: 1,
  useAllSpace: false,
  keepDeviceTypeOnOneRail: true,
  maxUnitsPerVerticalRail: 0,
  verticalDuctWidthMm: 40,
  emitVerticalDucts: true,
  emitRails: false,
  railWidthMm: 35,
};

export const withFlexibleDefaults = (opts) => ({ ...FLEXIBLE_DEFAULTS, ...(opts ?? {}) });

const FlexibleOptionsInputs = ({ flexibleOptions, onChange }) => {
  const opts = withFlexibleDefaults(flexibleOptions);
  const noLookahead = opts.sectionLookahead === 0;

  return (
    <div className="ssp-flexible-options">
      {/* ── Space usage ─────────────────────────────────────────────── */}
      <SettingGroup
        title="Space usage"
        intro="How sections share the width of the cabinet."
      >
        <SettingField
          id="ssp-lookahead"
          label="Sections to look ahead"
          hint="How many of the following sections may use free space next to a section."
          example={
            <ExampleBlock diagram={<LookaheadDiagram />}>
              With <b>2</b>, the two sections after <i>Protection devices</i> may be placed
              beside it if they fit. With <b>0</b>, every section starts its own band.
              Sections always keep their order and are never mixed.
            </ExampleBlock>
          }
        >
          <NumberInput
            id="ssp-lookahead"
            value={opts.sectionLookahead}
            min={0}
            max={10}
            integer
            onChange={(v) => onChange("sectionLookahead", v)}
          />
        </SettingField>

        <SettingToggle
          id="ssp-use-all-space"
          label="Use all available space"
          checked={opts.useAllSpace}
          disabled={noLookahead}
          onChange={(v) => onChange("useAllSpace", v)}
          hint={
            noLookahead
              ? "Needs \u201CSections to look ahead\u201D of 1 or more."
              : "Bands may grow taller so neighbouring sections fill the space better."
          }
          example={
            <ExampleBlock diagram={<UseAllSpaceDiagram />}>
              A section with a 300&nbsp;mm drive follows one with 90&nbsp;mm breakers.
              <b> Off:</b> the breakers get a low band, the drive its own band below.
              <b> On:</b> the band grows to 300&nbsp;mm and the breakers wrap into rows
              beside the drive.
            </ExampleBlock>
          }
        />
      </SettingGroup>

      {/* ── Vertical rails ──────────────────────────────────────────── */}
      <SettingGroup
        title="Vertical rails"
        intro={"Only for devices set to vertical (\u2195) or Auto in the layout tree."}
      >
        <SettingToggle
          id="ssp-keep-type"
          label="Keep each device type on one rail"
          checked={opts.keepDeviceTypeOnOneRail}
          onChange={(v) => onChange("keepDeviceTypeOnOneRail", v)}
          hint="Every article gets its own column(s); different types never share a column."
          example={
            <ExampleBlock diagram={<KeepTypeDiagram />}>
              35 terminals of type A and 5 of type B.
              <b> On:</b> two columns, 35 | 5.
              <b> Off:</b> one column with all 40, as long as the per-rail limit allows it.
            </ExampleBlock>
          }
        />

        <SettingField
          id="ssp-units-per-rail"
          label="Units per vertical rail"
          hint="Most units in one column; the rest continue in the next. 0 = no limit."
          example={
            <ExampleBlock diagram={<UnitsPerRailDiagram />}>
              60 terminals with <b>20</b> make 3 columns of 20. A device can override this
              in the tree (field <i>Per rail</i>). With <b>0</b> and &ldquo;Keep each device
              type on one rail&rdquo; off, rails are spread over short columns to keep the
              band low.
            </ExampleBlock>
          }
        >
          <NumberInput
            id="ssp-units-per-rail"
            value={opts.maxUnitsPerVerticalRail}
            min={0}
            integer
            unit="units"
            onChange={(v) => onChange("maxUnitsPerVerticalRail", v)}
          />
        </SettingField>
      </SettingGroup>

      {/* ── Vertical ducts ──────────────────────────────────────────── */}
      <SettingGroup title="Vertical ducts">
        <SettingToggle
          id="ssp-emit-vducts"
          label="Draw vertical ducts"
          checked={opts.emitVerticalDucts}
          onChange={(v) => onChange("emitVerticalDucts", v)}
          hint="Between sections, blocks and vertical rails. The space is reserved either way."
        />

        <SettingField
          id="ssp-vduct"
          label="Vertical duct width"
          hint="Used for every vertical duct, including those between vertical rails."
        >
          <NumberInput
            id="ssp-vduct"
            value={opts.verticalDuctWidthMm}
            min={0}
            unit="mm"
            onChange={(v) => onChange("verticalDuctWidthMm", v)}
          />
        </SettingField>
      </SettingGroup>

      {/* ── DIN rails ───────────────────────────────────────────────── */}
      {/* <SettingGroup title="DIN rails">
        <SettingToggle
          id="ssp-emit-rails"
          label="Show DIN rails"
          checked={opts.emitRails}
          onChange={(v) => onChange("emitRails", v)}
          hint="Draws the mounting rails in 2D and 3D. Doesn't change the layout."
        />

        <SettingField
          id="ssp-railw"
          label="Rail width"
          hint="35 mm is the standard top-hat rail."
          disabled={!opts.emitRails}
        >
          <NumberInput
            id="ssp-railw"
            value={opts.railWidthMm}
            min={1}
            unit="mm"
            disabled={!opts.emitRails}
            onChange={(v) => onChange("railWidthMm", v)}
          />
        </SettingField>
      </SettingGroup> */}
    </div>
  );
};

export default FlexibleOptionsInputs;
